import * as React from 'react';

/**
 * A custom hook that returns whether the prefers-reduced-motion media query has been set in the user's OS.
 *
 * ## Usage
 *
 * @example
 *
 * ```jsx
 * import React from 'react';
 * import {usePrefersReducedMotion} from '@packages/systems/spring';
 *
 * function Video({autoPlay, srcMp4}) {
 *   const prefersReducedMotion = usePrefersReducedMotion();
 *
 *   return (
 *    <video autoPlay={autoPlay && !prefersReducedMotion}>
 *      <source src={srcMp4} type="video/mp4" />
 *    </video>
 *   );
 * }
 * ```
 */

const QUERY = '(prefers-reduced-motion: no-preference)';

const getInitialState = () => {
  if (typeof window === 'undefined') return false;
  return !window.matchMedia(QUERY).matches;
};

export function usePrefersReducedMotion(): boolean {
  const [prefersReducedMotion, setPrefersReducedMotion] =
    React.useState(getInitialState);
  React.useEffect(() => {
    const mediaQueryList = window.matchMedia(QUERY);
    const listener = (event: MediaQueryListEventMap['change']) => {
      setPrefersReducedMotion(!event.matches);
    };
    mediaQueryList.addEventListener('change', listener);
    return () => {
      mediaQueryList.removeEventListener('change', listener);
    };
  }, []);
  return prefersReducedMotion;
}
